import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { getProducts, updateProductJson, deleteProduct } from "../api/products";
import {Checkbox} from "@radix-ui/themes/dist/esm";

function fmt(n){ return new Intl.NumberFormat("sk-SK",{style:"currency",currency:"EUR"}).format(n||0); }
function img(u){ return u || "/images/placeholder.jpg"; }
function toNum(v){ const n = parseFloat(v); return isNaN(n) ? null : n; }
function toInt(v){ const n = parseInt(v,10); return isNaN(n) ? null : Math.max(0,n); }

export default function ProductsTablePage(){
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    // edited: Map id -> { price?, quantity? }
    const [edited, setEdited] = useState({}); // { [id]: { price, quantity } }
    const editedCount = useMemo(()=> Object.keys(edited).length, [edited]);
    const [saving, setSaving] = useState(false);

    const [selected, setSelected] = useState(new Set()); // ids

    const [deltaQty, setDeltaQty] = useState(1); // o koľko prirátať/ubrať

    // hore vedľa „Pridať produkt“
    const allIds = items.map(p => p.id);
    const selectedCount = selected.size;

    const [pct, setPct] = useState("");      // percentá pre cenu
    const [qtySet, setQtySet] = useState(""); // nastaviť sklad na

    useEffect(() => {
        load();
    }, []);

    function load(){
        setLoading(true);
        getProducts()
            .then(setItems)
            .catch(() => setErr("Nepodarilo sa načítať produkty."))
            .finally(() => setLoading(false));
    }

    function markEdited(id, patch){
        setEdited(prev => ({ ...prev, [id]: { ...(prev[id]||{}), ...patch } }));
    }

    function handleChange(id, field, value){
        // okamžite zmeň hodnotu v tabuľke
        setItems(prev => prev.map(p => p.id===id ? { ...p, [field]: value } : p));
        // a označ na uloženie
        if (field === "price")     markEdited(id, { price: toNum(value) });
        if (field === "quantity")  markEdited(id, { quantity: toInt(value) });
    }

    async function handleSaveAll(){
        if (!editedCount) return;
        setSaving(true);
        try{
            const updates = Object.entries(edited).map(([id, patch]) => {
                // vyfiltruj len polia ktoré nie sú null
                const body = {};
                if (patch.price != null) body.price = patch.price;
                if (patch.quantity != null) body.quantity = patch.quantity;
                return updateProductJson(id, body);
            });
            await Promise.all(updates);
            setEdited({});
        }catch(e){
            alert("Niektoré zmeny sa nepodarilo uložiť.");
        }finally{
            setSaving(false);
            load();
        }
    }

    function handleCancelAll(){
        setEdited({});
        load();
    }

    async function handleDelete(p){
        if (!window.confirm(`Zmazať produkt „${p.name}“?`)) return;
        try{
            await deleteProduct(p.id);
            setItems(prev => prev.filter(x => x.id !== p.id));
            // aj keby bol v edited, odmaž ho
            setEdited(prev => { const copy = { ...prev }; delete copy[p.id]; return copy; });
        }catch(e){
            alert("Zmazanie sa nepodarilo.");
        }
    }

    function toggleOne(id){
        setSelected(prev => {
            const copy = new Set(prev);
            copy.has(id) ? copy.delete(id) : copy.add(id);
            return copy;
        });
    }
    function toggleAll(allIds){
        setSelected(prev => prev.size === allIds.length ? new Set() : new Set(allIds));
    }


    async function applyPricePercent(){ // sign = +1 alebo -1
        if (!selectedCount) return;
        // const factor = 1 + (sign * (Number(pct) || 0) / 100);
        const val = Math.max(0, Number(pct) || 0);
        // if (val < 0) return;
        setSaving(true);
        try{
            await Promise.all(
                items
                    .filter(p => selected.has(p.id))
                    // .map(p => updateProductJson(p.id, { price: Number((p.price * factor).toFixed(2)) }))
                    .map(p => updateProductJson(p.id, { price: val }))
            );
            setEdited({}); setSelected(new Set()); load();
        } finally { setSaving(false); }
    }

    async function applySetQuantity(){
        if (!selectedCount) return;
        const val = Math.max(0, parseInt(qtySet,10) || 0);
        setSaving(true);
        try{
            await Promise.all(
                items
                    .filter(p => selected.has(p.id))
                    .map(p => updateProductJson(p.id, { quantity: val }))
            );
            setEdited({}); setSelected(new Set()); load();
        } finally { setSaving(false); }
    }

    function normalizeInt(v) {
        var n = parseInt(v, 10);
        if (isNaN(n)) return 0;
        return n;
    }

    async function applyDeltaQuantity() {
        if (!selected.size) return;
        var d = normalizeInt(deltaQty); // môže byť aj záporné

        setSaving(true);
        try {
            var tasks = items
                .filter(function (p) { return selected.has(p.id); })
                .map(function (p) {
                    var current = normalizeInt(p.quantity ?? 0);
                    var next = current + d;
                    if (next < 0) next = 0; // nedovoľ záporný sklad
                    return updateProductJson(p.id, { quantity: next });
                });

            await Promise.all(tasks);
            setEdited({});
            setSelected(new Set());
            load();
        } catch (e) {
            alert("Zmena množstva sa nepodarila.");
        } finally {
            setSaving(false);
        }
    }

    async function deleteSelected() {
        if (!selected.size) return;
        if (!window.confirm("Naozaj zmazať vybrané produkty?")) return;

        setSaving(true);
        try {
            var tasks = items
                .filter(function (p) { return selected.has(p.id); })
                .map(function (p) { return deleteProduct(p.id); });

            await Promise.all(tasks);
            setEdited({});
            setSelected(new Set());
            load();
        } catch (e) {
            alert("Niektoré produkty sa nepodarilo zmazať.");
        } finally {
            setSaving(false);
        }
    }


    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h4 mb-0">Zoznam produktov</h1>
                <div className="d-flex gap-2">
                    <Link to="/produkt/novy" className="btn btn-brown">+ Pridať produkt</Link>
                </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="form-check">
                    <input className="form-check-input" type="checkbox"
                           checked={selected.size === allIds.length && allIds.length>0}
                           onChange={() => toggleAll(allIds)} />
                    <label className="form-check-label small">Vybrať všetko</label>
                </div>
                <div className="d-md-flex align-items-center gap-2">
                    <div className="admin-head input-group input-group-sm pb-1" style={{width:180}}>
                        <span className="input-group-text rounded-start-pill">Cena (€)</span>
                        <input type="number" min="0" className="form-control" value={pct} onChange={e=>setPct(e.target.value)} />
                        <button className="btn btn-sm btn-outline-success rounded-end-pill" disabled={!selectedCount || saving}
                                onClick={applyPricePercent}>OK</button>
                    </div>
                    <div className="admin-head input-group input-group-sm pb-1" style={{width:160}}>
                        <span className="input-group-text rounded-start-pill">Sklad =</span>
                        <input type="number" min="0" className="form-control" value={qtySet} onChange={e=>setQtySet(e.target.value)} />
                        <button className="btn btn-sm btn-outline-primary rounded-end-pill" disabled={!selectedCount || saving}
                                onClick={applySetQuantity}>OK</button>
                    </div>
                    <div className="admin-head input-group input-group-sm pb-1" style={{ width:140 }}>
                        <span className="input-group-text rounded-start-pill" >± ks</span>
                        <input
                            type="number"
                            className="form-control"
                            value={deltaQty}
                            onChange={function(e){ setDeltaQty(e.target.value); }}
                        />
                        <button
                            className="btn btn-sm btn-outline-primary rounded-end-pill"
                            disabled={!selected.size || saving}
                            onClick={applyDeltaQuantity}
                            title="Pripočítať/odpočítať ks"
                            type="button"
                        >
                            OK
                        </button>
                    </div>
                    <button
                        className="admin-head btn btn-success rounded-pill"
                        disabled={!editedCount || saving}
                        onClick={handleSaveAll}
                        title="Uložiť všetky zmeny"
                    >
                        {saving ? "Ukladám…" : `Uložiť zmeny (${editedCount})`}
                    </button>
                    {editedCount > 0 && (
                        <button
                            className="admin-head btn btn-outline-secondary rounded-pill"
                            onClick={handleCancelAll}
                        >
                            Zrušiť zmeny
                        </button>
                    )}
                    {/* Zmazať vybrané */}
                    <button
                        className="admin-head btn btn-outline-danger rounded-pill"
                        disabled={!selected.size || saving}
                        onClick={deleteSelected}
                        type="button"
                    >
                        Zmazať vybrané
                    </button>
                </div>
            </div>

            {err && <div className="alert alert-danger">{err}</div>}
            {loading ? (
                <div className="text-muted">Načítavam…</div>
            ) : (
                <div className="table-responsive">
                    <table className="table align-middle table-hover">
                        <thead className="table-light" style={{position:"sticky", top:0, zIndex:1}}>
                        <tr>
                            <th style={{width:36}}></th>
                            <th style={{width:72}}>Obr.</th>
                            <th style={{width:280}}>Názov</th>
                            <th style={{width:120}}>Cena (€)</th>
                            <th style={{width:100}}>Sklad</th>
                            <th style={{width:140}}>Kategória</th>
                            <th style={{width:130}}>Veľkosť</th>
                            <th style={{width:120}}>Drevo</th>
                            <th style={{width:160}}>Podkategória</th>
                            <th style={{width:210}} className="text-end">Akcie</th>
                        </tr>
                        </thead>
                        <tbody>
                        {items.map(p => (
                            <tr key={p.id} className={edited[p.id] ? "table-warning" : ""}>
                                <td>
                                    <input type="checkbox" className="form-check-input"
                                           checked={selected.has(p.id)} onChange={()=>toggleOne(p.id)} />
                                </td>
                                <td>
                                    <img
                                        src={img(p.imageUrl)}
                                        onError={e=> e.currentTarget.src="/images/placeholder.jpg"}
                                        alt={p.name}
                                        className="rounded border"
                                        style={{width:56, height:56, objectFit:"cover"}}
                                    />
                                </td>
                                <td>
                                    <div className="fw-semibold">{p.name}</div>
                                    <div className="small text-muted">{p.slug}</div>
                                </td>

                                {/* EDITOVATEĽNÉ: CENA */}
                                <td style={{maxWidth:160}}>
                                    <input
                                        type="number" step="1" min="0"
                                        className="form-control form-control-sm"
                                        value={p.price ?? ""}
                                        onChange={e => handleChange(p.id, "price", e.target.value)}
                                    />
                                </td>

                                {/* EDITOVATEĽNÉ: SKLAD */}
                                <td style={{maxWidth:140}}>
                                    <input
                                        type="number" min="0" step="1"
                                        className="form-control form-control-sm"
                                        value={p.quantity ?? 0}
                                        onChange={e => handleChange(p.id, "quantity", e.target.value)}
                                    />
                                </td>

                                <td>{p.category || "-"}</td>
                                <td>{p.size || "-"}</td>
                                <td>{p.wood || "-"}</td>
                                <td>{p.subcategory || "-"}</td>

                                <td className="text-end">
                                    <div className="btn-group">
                                        <Link to={`/produkt/${p.slug || p.id}`} className="btn btn-sm btn-outline-success">
                                            Detail
                                        </Link>
                                        <Link to={`/admin/produkt/${p.id}/upravit`} className="btn btn-sm btn-outline-warning">
                                            Upraviť
                                        </Link>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(p)}>
                                            Zmazať
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan="9" className="text-center text-muted py-4">Žiadne produkty</td></tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
